import React, { useState, useEffect } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Button } from '@raptormaps/button';
import { Text } from '@raptormaps/text';
import toastr from 'toastr';
import _ from 'lodash';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

import { Switch } from '@raptormaps/switch';
import FeatureApiClient from '../../apiClient/FeatureApiClient';
import styled from 'styled-components';

const GroupSection = styled.div`
  margin-bottom: 24px;
`;

const GroupLabel = styled.span`
  display: inline-block;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 2px 8px;
  border-radius: 4px;
  margin-bottom: 12px;
  background: ${({ $variant }) =>
    $variant === 'software' ? '#ebf4ff' : '#ebf8f0'};
  color: ${({ $variant }) => ($variant === 'software' ? '#2b4f9e' : '#276749')};
`;

const FeatureRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
  gap: 12px;
`;

const FeatureInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const SOFTWARE_TEAM_FLAGS = new Set([
  'Farmbuilder Autolocalization',
  'Farmbuilder Custom Numbering Mode',
  'Farmbuilder Delta T',
  'FB Transfer Point Anomalies',
  'FarmBuilder Image Pair List',
  'Farm & Inspection Sharing',
  'Use RM Ingestion',
  'Uploader Redesign',
  'FarmBuilder UX Guardrails',
]);

/**
 * Modal that is rendered when a user selects an org from the table view of org permissions.
 * Features are grouped into Product and Software Team sections.
 */
const PermissionModal = ({
  show,
  handleClose,
  selectedOrg,
  orgFeatures,
  setOrgFeatures,
  allFeatures,
  farmLevel = false,
}) => {
  const [featureDiff, setFeatureDiff] = useState([]);
  const [loading, setLoading] = useState(false);

  const isRVDeprecated = useFeatureIsOn('legacy-report-viewer-link');

  const flagsToRemove = [4, 9, 11, 13, 414];
  const reportViewerFlags = [183, 124, 123, 121, 120, 119, 118, 117];
  if (isRVDeprecated) {
    flagsToRemove.push(...reportViewerFlags);
  }
  const filteredFeatures = allFeatures.filter(
    f => !flagsToRemove.includes(f.id),
  );

  useEffect(() => {
    const enabledFeatures = new Set(orgFeatures.map(o => o.feature_id));
    setFeatureDiff(filteredFeatures.map(f => enabledFeatures.has(f.id)));
  }, [orgFeatures]);

  const handleToggle = index => {
    setFeatureDiff(prev =>
      prev.map((status, idx) => (idx === index ? !status : status)),
    );
  };

  const handleSave = async () => {
    setLoading(true);

    const zippedDiff = _.zip(filteredFeatures, featureDiff);
    const enabledFeatures = new Set(orgFeatures.map(o => o.feature_id));

    const featuresToDisable = zippedDiff.filter(
      ([feature, toggledStatus]) =>
        enabledFeatures.has(feature.id) && !toggledStatus,
    );
    const featuresToEnable = zippedDiff.filter(
      ([feature, toggledStatus]) =>
        !enabledFeatures.has(feature.id) && toggledStatus,
    );

    const featureApiClient = new FeatureApiClient();
    const key = farmLevel === true ? 'farmLevel' : 'orgLevel';
    const featureApi = {
      farmLevel: {
        enableFeature: featureApiClient.enableFarmFeature,
        disableFeature: featureApiClient.disableFarmFeature,
        params: { solar_farm_id: selectedOrg.id },
      },
      orgLevel: {
        enableFeature: featureApiClient.enableOrgFeature,
        disableFeature: featureApiClient.disableOrgFeature,
        params: { org_id: selectedOrg.id },
      },
    };

    try {
      if (!_.isEmpty(featuresToDisable)) {
        await Promise.all(
          featuresToDisable.map(([feature]) =>
            featureApi[key].disableFeature(selectedOrg.id, feature.id),
          ),
        );
      }
      if (!_.isEmpty(featuresToEnable)) {
        await Promise.all(
          featuresToEnable.map(([feature]) =>
            featureApi[key].enableFeature(selectedOrg.id, feature.id),
          ),
        );
      }
      toastr.success('Permissions Saved', 'Success', {
        timeOut: 3000,
        closeButton: true,
      });
    } catch (error) {
      console.error(error);
      toastr.error(error);
    }

    featureApiClient
      .getOrgFeatures(featureApi[key].params)
      .then(res => setOrgFeatures(res));

    setLoading(false);
  };

  const productFeatures = filteredFeatures
    .map((f, idx) => ({ feature: f, idx }))
    .filter(({ feature }) => !SOFTWARE_TEAM_FLAGS.has(feature.name));

  const softwareTeamFeatures = filteredFeatures
    .map((f, idx) => ({ feature: f, idx }))
    .filter(({ feature }) => SOFTWARE_TEAM_FLAGS.has(feature.name));

  const renderFeatureRow = ({ feature, idx }) => (
    <FeatureRow key={feature.id}>
      <FeatureInfo>
        <Text variant="paragraph_small_bold">{feature.name}</Text>
        {feature.description && (
          <Text variant="paragraph_small" color="neutral_600">
            {feature.description}
          </Text>
        )}
      </FeatureInfo>
      <Switch
        checked={featureDiff[idx]}
        onCheckedChange={() => handleToggle(idx)}
        disabled={loading}
        size="xlarge"
      />
    </FeatureRow>
  );

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <div className="permission-modal-title">
          {farmLevel && (
            <Modal.Title>
              Org: {selectedOrg.org_id} - {selectedOrg.org_name}
            </Modal.Title>
          )}
          <Modal.Title>
            # {selectedOrg.id} - {selectedOrg.name}
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
        {show && (
          <>
            {productFeatures.length > 0 && (
              <GroupSection>
                <GroupLabel $variant="product">Product</GroupLabel>
                {productFeatures.map(renderFeatureRow)}
              </GroupSection>
            )}
            {softwareTeamFeatures.length > 0 && (
              <GroupSection>
                <GroupLabel $variant="software">Software Team</GroupLabel>
                {softwareTeamFeatures.map(renderFeatureRow)}
              </GroupSection>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Close
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={loading}>
          {loading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />{' '}
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PermissionModal;
